import { useEffect, useRef } from "react";

import { FirebaseAuth } from "../../../utils/firebase";
import { CbhFeatureFlag, useCbhFlag } from "../../FeatureFlags";
import { APP_V2_APP_EVENTS, logError, logEvent } from "../../lib/analytics";
import { cognitoSignOut, getExistingSessionEmail } from "./cognitoAuth";
import { establishCognitoSessionFromFirebaseToken } from "./firebaseTokenExchange";

/**
 * Ensures that a Cognito session exists for the current user when the
 * `2026-03-admin-enable-cognito-in-api-calls` flag is enabled.
 *
 * Users who were already logged in via Firebase before the migration flag was
 * turned on have a valid Firebase session but no Cognito tokens in
 * localStorage. When the flag is enabled, API helpers try to send a Cognito
 * token, fail to find one, and silently fall back to Firebase — which is why
 * most users still appear as "Firebase" on the Datadog dashboard.
 *
 * This hook runs once when the flag first becomes `true` and, if no Cognito
 * session exists, exchanges the current Firebase ID token for a Cognito
 * session in the background. Failures are logged but never block the user.
 *
 * Mount this hook once, high in the component tree (next to
 * `useSyncCognitoApiTokenFlag`).
 */
export function useEnsureCognitoSession(): void {
  const hasAttempted = useRef(false);

  const isCognitoEnabled = useCbhFlag(CbhFeatureFlag.ADMIN_ENABLE_COGNITO_IN_API_CALLS, {
    defaultValue: false,
  });

  useEffect(() => {
    if (!isCognitoEnabled || hasAttempted.current) {
      return;
    }

    hasAttempted.current = true;

    void (async () => {
      try {
        const firebaseUser = FirebaseAuth.currentUser;
        if (!firebaseUser?.email) {
          return;
        }

        const existingCognitoEmail = await getExistingSessionEmail();
        if (existingCognitoEmail === firebaseUser.email) {
          return;
        }

        if (existingCognitoEmail) {
          await cognitoSignOut();
        }

        const firebaseIdToken = await firebaseUser.getIdToken();

        await establishCognitoSessionFromFirebaseToken({
          email: firebaseUser.email,
          firebaseIdToken,
        });
        logEvent(APP_V2_APP_EVENTS.PASSWORD_LOGIN_COGNITO_SESSION_SUCCESS, {
          source: "session-restore",
        });
      } catch (error: unknown) {
        logError(APP_V2_APP_EVENTS.PASSWORD_LOGIN_COGNITO_SESSION_FAILURE, {
          error,
          metadata: { source: "session-restore" },
        });
      }
    })();
  }, [isCognitoEnabled]);
}
